import {
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup
} from "@material-ui/core";
import _ from "lodash";
import { map } from "ramda";
import * as React from "react";
import LoadingSpinner from "src/shared/LoadingSpinner";
import QuiqButton from "src/shared/QuiqButton";
import CloseIcon from "../documentUpload/close.svg";
import CommentCard from "./CommentCard";
import CommentInput from "./CommentInput";
import "./CommentPopover.scss";
import { Comment } from "./types";
interface CommentPopoverProps {
  comments: Comment[] | null;
  buttonText?: string;
  onClose: (event?: React.SyntheticEvent<{}>) => void;
  updateComments: (comments: Comment[]) => void;
  shouldHideInput?: boolean;
  anchorEl?: HTMLElement | null;
  isOpen: boolean;
  spreadId?: string;
  accountId?: string;
  accountName?: string;
  dontPostToBackend?: boolean;
  dontShowAccountLabel?: boolean;
  canEditComments?: boolean;
  canDeleteComments?: boolean;
  inputPlaceholderText?: string;
  popoverTitle?: string;
  period?: string;
  periodSelectors?: string[];
  popoverSubtitle?: string;
  onOptionalConfirm?: () => void;
  hasFocus?: boolean;
}
interface CommentPopoverState {
  isEditingComment: boolean;
  commentBeingEdited: Comment | null;
  commentBeingDeleted: Comment | null;
  period?: string;
  optionalConfirmClicked: boolean;
}
export class CommentPopover extends React.Component<
  CommentPopoverProps,
  CommentPopoverState
> {
  constructor(props: CommentPopoverProps) {
    super(props);
    this.state = {
      isEditingComment: false,
      commentBeingEdited: null,
      commentBeingDeleted: null,
      period: this.props.period,
      optionalConfirmClicked: false
    };
  }
  public render() {
    const {
      comments,
      shouldHideInput,
      anchorEl,
      isOpen,
      hasFocus
    } = this.props;
    const { commentBeingEdited } = this.state;
    return (
      <div className="CommentPopover">
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          classes={{ paper: "CommentPopoverPaper" }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center"
          }}
          onClose={this.props.onClose}
          onClick={this.stopPropogation}
          onExited={this.onExited}
        >
          <div className="CommentPopover__header">
            <div className="CommentPopover__header__title">
              {this.props.popoverTitle ||
                (comments && comments.length === 0 && !shouldHideInput
                  ? "Add Comment"
                  : "Comments")}
            </div>
            <img
              src={CloseIcon}
              className="CommentPopover__header__close-icon"
              onClick={this.props.onClose}
            />
          </div>
          <div className="CommentPopover__subtitle">
            {this.props.popoverSubtitle}
          </div>
          <div className="CommentPopover__content">{this.renderComments()}</div>
          <CommentInput
            spreadId={this.props.spreadId}
            accountId={this.props.accountId}
            accountName={this.props.accountName}
            className="CommentPopover__input"
            updateComments={this.props.updateComments}
            comments={this.props.comments}
            dontPostToBackend={this.props.dontPostToBackend}
            exitEditingMode={this.exitEditingMode}
            commentBeingEdited={this.state.commentBeingEdited}
            commentBeingDeleted={this.state.commentBeingDeleted}
            onClose={this.props.onClose}
            isHidden={shouldHideInput && !Boolean(commentBeingEdited)}
            buttonText={"Submit"}
            placeholder={this.props.inputPlaceholderText}
            period={this.state.period}
            hasFocus={hasFocus}
          />
          {this.props.periodSelectors && [
            <RadioGroup
              onChange={this.handlePeriodChange}
              name="periods"
              row={true}
              key="period-radio-group"
              value={this.state.period}
            >
              {this.props.periodSelectors.map((period, index) => (
                <FormControlLabel
                  value={period}
                  control={
                    <Radio
                      color="primary"
                      checked={this.state.period === period}
                      onClick={this.onRadioChange}
                    />
                  }
                  label={period}
                  className="CommentPopover__periods"
                  key={`comment-period-${index}`}
                />
              ))}
            </RadioGroup>,
            <QuiqButton
              color="green"
              key="optional-confirm-button"
              className="CommentPopover__optional-confirm-button"
              onClick={this.handleOptionalConfirmClick}
              isDisabled={
                this.props.comments && this.props.comments.length < 1
                  ? true
                  : false
              }
              isLoading={this.state.optionalConfirmClicked}
            >
              {this.props.buttonText || "Return for Review"}
            </QuiqButton>
          ]}
        </Popover>
      </div>
    );
  }
  public editComment = (comment: Comment) => {
    this.setState({ commentBeingEdited: comment });
  };
  public deleteComment = (comment: Comment) => {
    this.setState({ commentBeingDeleted: comment }, () =>
      this.setState({ commentBeingDeleted: null })
    );
  };
  public exitEditingMode = () => {
    this.setState({ commentBeingEdited: null });
  };
  private handleOptionalConfirmClick = () => {
    this.setState({ optionalConfirmClicked: true });
    if (this.props.onOptionalConfirm) {
      this.props.onOptionalConfirm();
    }
  };
  private handlePeriodChange = (
    event: React.ChangeEvent<{}>,
    value: string
  ) => {
    if (this.state.period === value) {
      this.setState({ period: undefined });
    } else {
      this.setState({ period: value });
    }
  };
  private onRadioChange = (event: React.ChangeEvent<{}>) => {
    if (this.state.period === _.get(event.target, "value")) {
      this.setState({ period: undefined });
    } else {
      this.setState({ period: _.get(event.target, "value") });
    }
  };
  private stopPropogation = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };
  private onExited = () => {
    this.exitEditingMode();
  };
  private renderComments = () => {
    const commentPopoverType = this.props.accountId ? "account" : "spread";
    if (this.props.comments === null) {
      return <LoadingSpinner />;
    }
    if (this.props.comments.length < 1 && this.props.shouldHideInput) {
      return (
        <span className="CommentPopover__content__message">
          {`There are no comments for this ${commentPopoverType}`}
        </span>
      );
    }
    if (this.props.comments.length < 1 && !this.props.shouldHideInput) {
      return;
    }
    if (this.state.commentBeingEdited) {
      return;
    }
    return (
      <div className="CommentPopover__CommentCards">
        {map(
          (comment: Comment) => (
            <CommentCard
              comment={comment}
              key={comment.id || comment.body}
              dontShowAccountLabel={this.props.dontShowAccountLabel}
              canEditComments={this.props.canEditComments}
              editComment={this.editComment}
              deleteComment={this.deleteComment}
              canDeleteComments={this.props.canDeleteComments}
            />
          ),
          this.props.comments
        )}
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/commentView/CommentPopover.tsx